<template>
    <div class="space-y-8">
        <h2 class="text-xl font-semibold text-center outline-none">{{ $t('views.feed.reconciliations.newModal.title') }}</h2>
        <div class="space-y-2">
            <CustomSelect
                v-model:value="computedValue.location"
                :label="$t('views.feed.reconciliations.newModal.locationFieldLabel')"
                return-object
                :groups="true"
                :items="locationOptions"
                item-text="name"
                item-value="name"
                group-key="barns"
                group-text-key="name"
                :group-select-options="farmSelectOptions"
                sub-group-key="batches"
                sub-group-text-key="name"
                :sub-group-select-options="barnSelectOptions"
                :sub-group-item-select-options="batchSelectOptions"
                :show-required-indication="false"
                :allow-empty="false"
            ></CustomSelect>
            <CustomSelect
                v-model:value="computedValue.fromStatusDate"
                :label="$t('views.feed.reconciliations.newModal.fromDateFieldLabel')"
                :items="fromDateOptions"
                item-text="label"
                item-value="value"
                :disabled="isBatchSelected || !computedValue.location"
                :show-required-indication="false"
                :allow-empty="false"
            ></CustomSelect>
            <CustomSelect
                v-model:value="computedValue.toStatusDate"
                :label="$t('views.feed.reconciliations.newModal.toDateFieldLabel')"
                :items="toDateOptions"
                item-text="label"
                item-value="value"
                :disabled="isBatchSelected || !computedValue.fromStatusDate"
                :show-required-indication="false"
                :allow-empty="false"
            ></CustomSelect>
        </div>
        <div v-if="noToDateOptionsAvailable" class="flex items-center gap-2 rounded-md bg-yellow-50 px-3 py-2 text-sm text-yellow-800 ring-1 ring-yellow-300">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 shrink-0" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path fill-rule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z" clip-rule="evenodd" />
            </svg>
            <span>{{ $t('views.feed.reconciliations.newModal.allPeriodsReconciled') }}</span>
        </div>
        <div class="flex justify-end">
            <CustomButton class="min-w-44" :disabled="!canProceed" @click="onContinueClicked">
                {{ $t('views.feed.reconciliations.newModal.continueButton') }}
            </CustomButton>
        </div>
    </div>
</template>

<script lang="ts">
import { defineComponent, computed, watch, ref } from 'vue';
import { Form } from '@makeabledk/vue-ui/support/http';
import CustomSelect from '@/components/ui/CustomSelect.vue';
import CustomButton from '@/components/ui/CustomButton.vue';
import { store } from '@/plugins/store';
import { ActionType } from '@/plugins/store/actions';
import { i18n } from '@/plugins/internationalization/i18n';
import { Barn, DirectoryBatch, FeedReconciliation, FeedReconciliationValidDates } from '@/types';

const ALL_DATES_FROM = '1900-01-01';
const ALL_DATES_TO = '2099-12-31';

export default defineComponent({
    components: { CustomSelect, CustomButton },
    props: {
        value: {
            type: Object as () => Form<any> & any,
            required: true,
        },
    },
    emits: ['update:value', 'submit', 'close'],
    setup(props, { emit }) {
        const computedValue = computed({
            get() {
                return props.value;
            },
            set(newValue: any) {
                emit('update:value', newValue);
            },
        });

        const validDates = ref<string[]>([]);
        const existingReconciliations = ref<FeedReconciliation[]>([]);
        let locationRequestGen = 0;

        function formatDate(dateString: string): string {
            return i18n.global.d(new Date(dateString), 'short');
        }

        const locationOptions = computed(() =>
            store.state.farms.map((farm) => ({
                name: farm.name,
                farm_id: farm.farm_id,
                barns: store.state.barns
                    .filter((b: Barn) => b.farm_id === farm.farm_id)
                    .map((barn: Barn) => ({
                        name: barn.name,
                        barn_id: barn.barn_id,
                        is_batch_production: barn.is_batch_production,
                        batches: barn.is_batch_production
                            ? store.state.batches
                                  .filter((b: DirectoryBatch) => b.barn.id === barn.barn_id)
                                  .map((b: DirectoryBatch) => ({
                                      name: b.name,
                                      locationType: 'batch' as const,
                                      locationId: b.id,
                                      startDate: b.start_date,
                                      endDate: b.end_date,
                                      startOn: b.start_on,
                                      endOn: b.end_on,
                                      isOpen: b.end_date === null,
                                  }))
                                  .sort((a, b) => (a.isOpen === b.isOpen ? 0 : a.isOpen ? 1 : -1))
                            : [],
                    })),
            }))
        );

        const farmSelectOptions = {
            selectableCondition: () => true,
            intermediateSelectableCondition: () => false,
            value: (farm: any) => ({ locationType: 'farm', locationId: farm.farm_id, name: farm.name }),
        };

        const barnSelectOptions = {
            selectableCondition: () => true,
            intermediateSelectableCondition: () => false,
            value: (barn: any) => ({ locationType: 'barn', locationId: barn.barn_id, name: barn.name }),
        };

        const batchSelectOptions = {
            selectableCondition: (batch: any) => !batch.isOpen,
        };

        const isBatchSelected = computed(() => props.value?.location?.locationType === 'batch');

        // Mirrors the backend overlap rule (FeedReconciliationsRepository.HasOverlap):
        // a candidate period [from, to] conflicts with an existing reconciliation when
        // existing.from < to && existing.to > from. Strict comparisons mean consecutive
        // periods sharing a boundary status date are allowed, while genuine overlaps —
        // including a period that spans/covers existing ones — are not. Dates are
        // 'YYYY-MM-DD', so lexicographic string comparison is chronologically correct.
        function periodOverlapsExisting(from: string, to: string): boolean {
            return existingReconciliations.value.some(
                (r) => r.from_status_date < to && r.to_status_date > from
            );
        }

        // A status date cannot start a new period if it falls strictly inside an existing
        // reconciliation [from, to) — every later to-date would overlap it. The end
        // boundary (d === existing.to) stays selectable so a consecutive period is allowed.
        function isInsideExistingPeriod(d: string): boolean {
            return existingReconciliations.value.some(
                (r) => r.from_status_date <= d && d < r.to_status_date
            );
        }

        const fromDateOptions = computed(() => {
            const lastDate = validDates.value[0];
            return validDates.value
                .filter((d) => (isBatchSelected.value ? true : d !== lastDate && !isInsideExistingPeriod(d)))
                .map((d) => ({ label: formatDate(d), value: d }));
        });

        const toDateOptions = computed(() => {
            const fromDate = props.value?.fromStatusDate ?? '';
            return validDates.value
                .filter((d) => d > fromDate && !periodOverlapsExisting(fromDate, d))
                .map((d) => ({ label: formatDate(d), value: d }));
        });

        const noToDateOptionsAvailable = computed(() => {
            const fromDate = props.value?.fromStatusDate ?? '';
            return !!fromDate && toDateOptions.value.length === 0;
        });

        watch(
            () => props.value?.location,
            async (location) => {
                const myGen = ++locationRequestGen;
                // Direct mutation is intentional: props.value is a Form<T> mutable object owned by the parent
                props.value.fromStatusDate = '';
                props.value.toStatusDate = '';
                validDates.value = [];
                existingReconciliations.value = [];

                if (!location) return;

                if (location.locationType === 'batch') {
                    const startDate = location.startOn || '';
                    const endDate = location.endOn || '';
                    validDates.value = [startDate, endDate].filter(Boolean);
                    props.value.fromStatusDate = startDate;
                    props.value.toStatusDate = endDate;
                } else {
                    try {
                        const [validDatesResult, reconciliations] = await Promise.all([
                            store.dispatch(ActionType.GetFeedReconciliationValidDates, { locationId: location.locationId }),
                            store.dispatch(ActionType.GetFeedReconciliationsByLocation, {
                                locationId: location.locationId,
                                fromDate: ALL_DATES_FROM,
                                toDate: ALL_DATES_TO,
                            }),
                        ]);
                        if (myGen !== locationRequestGen) return;
                        validDates.value = ((validDatesResult as FeedReconciliationValidDates)?.valid_status_dates ?? []).slice().sort().reverse();
                        existingReconciliations.value = Array.isArray(reconciliations) ? reconciliations : [];
                    } catch {
                        // Dates remain empty; user can retry by reselecting a location
                    }
                }
            }
        );

        watch(
            () => props.value?.fromStatusDate,
            (newVal, oldVal) => {
                if (!isBatchSelected.value && newVal !== oldVal && props.value?.toStatusDate) {
                    props.value.toStatusDate = '';
                }
            }
        );

        const canProceed = computed(() => {
            const form = props.value;
            return (
                form?.location !== null &&
                form?.location !== undefined &&
                form?.fromStatusDate !== '' &&
                form?.toStatusDate !== '' &&
                new Date(form.fromStatusDate) <= new Date(form.toStatusDate) &&
                !noToDateOptionsAvailable.value
            );
        });

        function onContinueClicked() {
            if (canProceed.value) {
                emit('submit');
            }
        }

        return {
            computedValue,
            validDates,
            existingReconciliations,
            fromDateOptions,
            toDateOptions,
            noToDateOptionsAvailable,
            locationOptions,
            farmSelectOptions,
            barnSelectOptions,
            batchSelectOptions,
            isBatchSelected,
            canProceed,
            onContinueClicked,
        };
    },
});
</script>
